import type { LanguageModel } from '@repo/common/types/Download';
import { getTranscribeModels } from '@repo/common/types/Download';
import { userDataModel } from '@repo/sharedlib/src/stores/auth.store';
import { derived, get, writable } from 'svelte/store';
import { getHandler } from '../sharedlib';
import constants from '../utils/constants';

const defaultTranscribeOptions = getTranscribeModels();

export const transcribeOptions = writable<LanguageModel[]>(defaultTranscribeOptions);

export const recalculateOptions = async () => {
  const $transcribeOptions = get(transcribeOptions);

  const options = await Promise.all(
    $transcribeOptions.map(async (option) => {
      option.downloaded = await getHandler().modelIsDownloaded(option);
      return option;
    })
  );
  transcribeOptions.set(options);
};

// Create a config proxy store that can be set later
export const configProxy = writable<any>(null);

export const selectedTranscribeOptions = derived(
  [transcribeOptions, configProxy],
  ([$transcribeOptions, $config]) => {
    if (!$config) {
      console.log('selectedTranscribeOptions: no config');
      return {
        loaded: false,
        rtTranscribeModel: null,
        finalTranscribeModel: null
      };
    }
    const modelsSet = !!($config.rtTranscribeModel && $config.finalTranscribeModel);
    if (modelsSet) {
      const rtTranscribeModel = $transcribeOptions.find(
        (option) => option.id === $config.rtTranscribeModel
      );
      const finalTranscribeModel = $transcribeOptions.find(
        (option) => option.id === $config.finalTranscribeModel
      );
      return {
        loaded:
          rtTranscribeModel?.downloaded !== undefined &&
          finalTranscribeModel?.downloaded !== undefined,
        rtTranscribeModel,
        finalTranscribeModel
      };
    }

    // Default to small model for real-time, large model for final processing
    const defaultRtModel = $transcribeOptions.find((option) => option.id === 'ggml-small-q5_1');
    const defaultFinalModel = $transcribeOptions.find(
      (option) => option.id === 'ggml-large-v3-turbo-q8_0'
    );

    return {
      loaded: false,
      rtTranscribeModel: defaultRtModel || null,
      finalTranscribeModel: defaultFinalModel || null
    };
  }
);
export const refreshPermissions = async (showModal: boolean = false) => {
  return await getHandler().checkPermissions(showModal);
};

// Store the unsubscribe function to clean up on HMR
let configProxyUnsubscribe: (() => void) | null = null;

// Initialize config proxy when config store is available
export const initializeConfigProxy = async () => {
  try {
    // Clean up existing subscription if any
    if (configProxyUnsubscribe) {
      configProxyUnsubscribe();
      configProxyUnsubscribe = null;
    }

    const { config } = await import('./config');
    configProxyUnsubscribe = config.subscribe((value) => {
      configProxy.set(value);
    });
  } catch (error) {
    console.warn('Failed to initialize config proxy:', error);
  }
};

export const userOnboardingDataLoaded = writable<boolean>(false);

export const profileValidated = derived(
  [userDataModel, userOnboardingDataLoaded],
  ([$userDataModel, $userOnboardingDataLoaded]) => {
    if (!$userOnboardingDataLoaded) {
      console.log('profileValidated: no user onboarding data loaded');
      return null;
    }

    console.log('profileValidated', $userDataModel.item.profile);
    return true;
  }
);

export const validateConfig = async (): Promise<boolean | null> => {
  if (constants.PLATFORM === 'web') {
    // Web app doesn't require local model downloads.
    return true;
  }
  await recalculateOptions();
  await initializeConfigProxy();
  const $selectedTranscribeOptions = get(selectedTranscribeOptions);
  if (!$selectedTranscribeOptions.loaded) {
    return null;
  }
  return !!(
    $selectedTranscribeOptions.rtTranscribeModel?.downloaded &&
    $selectedTranscribeOptions.finalTranscribeModel?.downloaded
  );
};

export const validatePermissions = async (): Promise<boolean | null> => {
  const $userOnboardingDataLoaded = get(userOnboardingDataLoaded);

  if (!$userOnboardingDataLoaded) {
    return null;
  }

  const permissions = await refreshPermissions(false);

  // Handle both old boolean format and new PermissionStatus format
  const screenRecording = typeof permissions === 'boolean' ? permissions : permissions.screenRecording;
  const microphone = typeof permissions === 'boolean' ? permissions : permissions.microphone;
  // Only check screen recording permission (used for system audio capture)
  const allGranted = screenRecording;

  setupNavigation.update((nav) => {
    nav.permissionValidated = allGranted;
    nav.screenRecordingPermission = screenRecording;
    nav.microphonePermission = microphone;
    return nav;
  });
  return allGranted;
};

/* Ensures the user's onboarding is complete.
 * This is a blocking call that will wait for all the tasks to complete.
 */
export const validateOnboarding = async () => {
  const $profileValidated = get(profileValidated);
  const configValidated = await validateConfig();
  const permissionsValidated = await validatePermissions();

  setupNavigation.update((nav) => {
    nav.profileValidated = $profileValidated;
    nav.configValidated = configValidated;
    nav.permissionValidated = permissionsValidated;
    return nav;
  });
  console.log('validateOnboarding', JSON.stringify(get(setupNavigation), null, 2));

  return $profileValidated && configValidated && permissionsValidated;
};

export const refreshOnboardingStatus = async () => {
  await Promise.all([refreshPermissions(), recalculateOptions()]);
};

export const setupNavigation = writable<{
  profileSetup: boolean | null;
  profileValidated: boolean | null;
  configSetup: boolean | null;
  configValidated: boolean | null;
  permissionValidated: boolean | null;
  screenRecordingPermission: boolean | null;
  microphonePermission: boolean | null;
}>({
  profileSetup: null,
  profileValidated: null,
  configSetup: null,
  configValidated: null,
  permissionValidated: null,
  screenRecordingPermission: null,
  microphonePermission: null
});

export const setupRequired = derived(
  [setupNavigation],
  ([$setupNavigation]) =>
    $setupNavigation.profileValidated === false ||
    $setupNavigation.configValidated === false ||
    $setupNavigation.permissionValidated === false
);

export const checkSetupRequired = () => {
  validateOnboarding();
  setupNavigation.update((nav) => {
    nav.profileSetup = nav.profileValidated === false;
    nav.configSetup = nav.configValidated === false;
    return nav;
  });
  console.log('checkSetupRequired: ', get(setupNavigation));
};

export const setupInProgress = derived(setupNavigation, ($setupNavigation) => {
  const result =
    $setupNavigation.profileSetup === true ||
    $setupNavigation.configSetup === true ||
    $setupNavigation.permissionValidated === false;
  return result;
});

export const resetOnboarding = () => {
  userOnboardingDataLoaded.set(false);
  setupNavigation.set({
    profileSetup: null,
    profileValidated: null,
    configSetup: null,
    configValidated: null,
    permissionValidated: null,
    screenRecordingPermission: null,
    microphonePermission: null
  });
};

// Store for permission modal display
export const permissionModalOpen = writable<boolean>(false);
